import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Work } from './pages/Work';
import { WorkPrint } from './pages/WorkPrint';
import { Commits } from './pages/Commits';
import { CommitView } from './pages/CommitView';
import { Login } from './pages/Login';
import { AdminUsers } from './pages/AdminUsers';
import { useCurrentUser } from './hooks/useCurrentUser';
import { apiClient } from './lib/api';
import { getClientConfig } from './lib/config';

export default function App() {
  const { data: user, isLoading } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? 'bg-atg-navy text-white' : 'text-slate-700 hover:bg-slate-200'
    }`;

  async function signOut() {
    const cfg = await getClientConfig();
    if (cfg.authProvider === 'Okta' || cfg.authProvider === 'Entra') {
      // Server-side OIDC sign-out: clears the local cookie, ends the IdP session, and
      // redirects back to the app root. Top-level navigation, not an XHR.
      window.location.assign('/api/auth/signout');
      return;
    }
    try {
      await apiClient.post('/api/auth/logout', {});
    } catch {
      // ignore — clear local state regardless
    }
    sessionStorage.removeItem('access_token');
    await qc.invalidateQueries({ queryKey: ['me'] });
    navigate('/login');
  }

  // The login page is always reachable, even while unauthenticated. Render it standalone
  // (it is a full-screen page, not part of the app shell).
  if (location.pathname === '/login') {
    return <Login />;
  }

  // Auth gate: until we know who the user is, hold the render. Once we know they are not
  // authenticated, send them to the dedicated /login page rather than the app shell.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading...
      </div>
    );
  }

  if (!user?.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Print-to-PDF view renders outside the app shell so no header/nav/footer reaches the PDF.
  if (location.pathname === '/print') {
    return <WorkPrint />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-6">
          <div className="flex items-center gap-2 text-lg font-bold text-atg-navy">
            <img src="/favicon.svg" alt="" width={28} height={28} aria-hidden="true" />
            Thunderdome
          </div>
          <nav className="flex gap-1">
            <NavLink to="/" end className={navClass}>Work</NavLink>
            <NavLink to="/commits" className={navClass}>Commits</NavLink>
            {/* Users link hidden: in Okta mode its role assignments never reach sign-in claims. Route kept. */}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm text-slate-600">
            <span>{user.email}</span>
            <button onClick={signOut} className="text-atg-blue hover:underline">Sign out</button>
          </div>
        </div>
      </header>
      <main className="flex-1 mx-auto w-full py-6">
        <Routes>
          <Route path="/" element={<Work />} />
          <Route path="/commits" element={<Commits />} />
          <Route path="/commits/:id" element={<CommitView />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin/users"
            element={
              <div className="max-w-7xl mx-auto px-4">
                <AdminUsers />
              </div>
            }
          />
        </Routes>
      </main>
      <footer className="border-t border-slate-200 py-3 text-center text-xs text-slate-500">
        RapidDevPlatform shell &middot; Thunderdome
      </footer>
    </div>
  );
}
